/**
 * Join timeline: how long a room takes from the tap to the first remote video.
 *
 * On native the queue middleware survives the navigation, so it can time the
 * stages from the timestamp it took when it dispatched the room. On web the
 * room is a full page load: that timestamp (and the middleware) die with the
 * page, which is why no web signaling/media/remote-video event was ever
 * recorded — only the boot breadcrumb. The marker below is written to
 * localStorage by whoever starts the navigation (the home page, the
 * interpreter portal, or the middleware itself), read back by the room page
 * when it boots, and removed once the timeline is complete or stale.
 *
 * Everything here is pure so it can be unit-tested without a browser; the
 * call sites do the storage and Performance API reads.
 */
export const JOIN_TIMELINE_KEY = 'vrs_join_timeline';
/** A marker older than this belongs to some earlier navigation. */
export const JOIN_TIMELINE_TTL_MS = 5 * 60 * 1000;
/** A portal moment before the navigation's start, and not over an hour before it. */
const PORTAL_MOMENT_MAX_AGE_MS = 60 * 60 * 1000;
function portalMoment(value, startedAt) {
    return typeof value === 'number' && Number.isFinite(value) && value <= startedAt
        && startedAt - value <= PORTAL_MOMENT_MAX_AGE_MS ? value : undefined;
}
export function serializeJoinMarker(marker) {
    return JSON.stringify({
        acceptTappedAt: marker.acceptTappedAt,
        callId: marker.callId,
        requestId: marker.requestId,
        requestedAt: marker.requestedAt,
        roomName: marker.roomName,
        source: marker.source,
        startedAt: marker.startedAt
    });
}
/**
 * Parse a stored marker. Returns null when it is missing, malformed, stale,
 * or for a different room than the one the page is showing.
 */
export function parseJoinMarker(raw, now, roomName) {
    if (!raw) {
        return null;
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!parsed || typeof parsed !== 'object') {
        return null;
    }
    const record = parsed;
    const startedAt = typeof record.startedAt === 'number' ? record.startedAt : NaN;
    const markerRoom = typeof record.roomName === 'string' ? record.roomName : '';
    if (!Number.isFinite(startedAt) || !markerRoom) {
        return null;
    }
    if (now - startedAt > JOIN_TIMELINE_TTL_MS || startedAt - now > 60000) {
        return null;
    }
    if (roomName && markerRoom !== roomName) {
        return null;
    }
    return {
        acceptTappedAt: portalMoment(record.acceptTappedAt, startedAt),
        callId: typeof record.callId === 'string' ? record.callId : undefined,
        requestId: typeof record.requestId === 'string' ? record.requestId : undefined,
        requestedAt: portalMoment(record.requestedAt, startedAt),
        roomName: markerRoom,
        source: typeof record.source === 'string' && record.source ? record.source : 'unknown',
        startedAt
    };
}
function round(value) {
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : undefined;
}
/**
 * Describe the room page load from the tap marker plus the browser's own
 * timings. `timeOrigin` is performance.timeOrigin (wall-clock ms when the
 * document started); the navigation/resource numbers are relative to it.
 *
 * `activationStart` (PerformanceNavigationTiming.activationStart, on the same
 * clock) is when a prerendered page was shown. Its timeOrigin is when the
 * prerender started, before the tap, so every stage is measured from the
 * moment it was shown instead: the tap → navigation gap is then the real one,
 * never a negative value rounded to 0, and work done before it counts as 0.
 */
export function describeRoomLoad({ activationStart, marker, now, timeOrigin, navigation, bundle }) {
    const shownAt = typeof activationStart === 'number' && Number.isFinite(activationStart) && activationStart > 0
        ? activationStart : 0;
    const origin = timeOrigin + shownAt;
    const bootOffset = now - origin;
    const sinceShown = (value) => (typeof value === 'number' ? Math.max(0, value - shownAt) : value);
    const responseEnd = sinceShown(navigation?.responseEnd);
    const rawDomReady = navigation?.domContentLoadedEventEnd;
    const domReady = typeof rawDomReady === 'number' && rawDomReady > 0 ? sinceShown(rawDomReady) : rawDomReady;
    const report = {
        source: marker?.source || 'unknown',
        tapToBootMs: round(marker ? now - marker.startedAt : bootOffset) ?? 0,
        navigationToBootMs: round(bootOffset) ?? 0,
        navigationType: navigation?.type,
        tapToNavigationMs: marker ? round(origin - marker.startedAt) : undefined,
        requestToNavigationMs: typeof marker?.requestedAt === 'number' ? round(origin - marker.requestedAt) : undefined,
        acceptTapToNavigationMs: typeof marker?.acceptTappedAt === 'number'
            ? round(origin - marker.acceptTappedAt) : undefined,
        navigationToResponseMs: round(responseEnd),
        responseToDomReadyMs: typeof responseEnd === 'number' && typeof domReady === 'number' && (rawDomReady ?? 0) > 0
            ? round(domReady - responseEnd)
            : undefined,
        domReadyToBootMs: typeof domReady === 'number' && (rawDomReady ?? 0) > 0 ? round(bootOffset - domReady) : undefined
    };
    if (shownAt > 0) {
        report.entryMode = 'prerender';
        report.activationStartMs = round(shownAt);
    }
    if (bundle) {
        report.bundleDurationMs = round(bundle.duration);
        report.bundleTransferBytes = round(bundle.transferSize);
        report.bundleEncodedBytes = round(bundle.encodedBodySize);
        if (typeof bundle.transferSize === 'number' && typeof bundle.encodedBodySize === 'number') {
            report.bundleFromCache = bundle.transferSize === 0 && bundle.encodedBodySize > 0;
        }
    }
    return report;
}
/** Pick the main app bundle out of the resource timing list. */
export function findBundleTiming(resources) {
    if (!resources) {
        return undefined;
    }
    return resources.find(entry => typeof entry.name === 'string' && /app\.bundle(\.min)?\.js/.test(entry.name));
}
